<template>
  <div class="mmg-storefront mmg-news-page fusion-body">
    <div id="wrapper">
      <SiteHeader />

      <div
        class="fusion-page-title-bar fusion-page-title-bar-none fusion-page-title-bar-left"
      >
        <div class="fusion-page-title-row">
          <div class="fusion-page-title-wrapper">
            <div class="fusion-page-title-captions">
              <h1 class="entry-title">{{ $t('news.title') }}</h1>
            </div>
            <div class="fusion-page-title-secondary">
              <div class="fusion-breadcrumbs">
                <router-link to="/">{{ $t('common.home') }}</router-link>
                <span class="fusion-breadcrumb-sep">/</span>
                <span class="breadcrumb-leaf">{{ $t('news.title') }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <main id="main" role="main" class="clearfix width-100">
        <div class="fusion-row news-row-max">
          <section id="content" class="full-width news-content">
            <article class="page type-page status-publish hentry">
              <div class="post-content">
                <section class="fusion-fullwidth news-section">
                  <!-- 列表视图 -->
                  <template v-if="!activeId">
                    <p class="news-intro">{{ $t('news.intro') }}</p>

                    <p v-if="loading" class="news-empty">{{ $t('news.loading') }}</p>
                    <p v-else-if="loadError" class="news-empty">{{ loadError }}</p>
                    <p v-else-if="!newsItems.length" class="news-empty">{{ $t('news.empty') }}</p>

                    <ul v-else class="news-list" role="list">
                      <li
                        v-for="item in newsItems"
                        :key="item.id"
                        class="news-item"
                      >
                        <button
                          type="button"
                          class="news-item__row"
                          @click="openNews(item.id)"
                        >
                          <time class="news-item__date" :datetime="item.date">
                            {{ formatDate(item.date) }}
                          </time>
                          <span class="news-item__title">{{ item.title }}</span>
                          <i
                            class="fa fa-chevron-right news-item__chevron"
                            aria-hidden="true"
                          />
                        </button>
                      </li>
                    </ul>

                    <section class="order-notice" aria-labelledby="order-notice-title">
                      <h2 id="order-notice-title" class="order-notice__title">
                        {{ $t('news.orderNotice.title') }}
                      </h2>

                      <div class="order-notice__block">
                        <h3 class="order-notice__heading">
                          {{ $t('news.orderNotice.customizationTitle') }}
                        </h3>
                        <ul class="order-notice__list">
                          <li
                            v-for="(item, index) in orderNoticeCustomizationItems"
                            :key="'custom-' + index"
                          >
                            {{ item }}
                          </li>
                        </ul>
                      </div>

                      <div class="order-notice__block">
                        <h3 class="order-notice__heading">
                          {{ $t('news.orderNotice.deliveryTitle') }}
                        </h3>
                        <p class="order-notice__text">{{ $t('news.orderNotice.deliveryText') }}</p>
                      </div>

                      <div class="order-notice__block">
                        <h3 class="order-notice__heading">
                          {{ $t('news.orderNotice.paymentTitle') }}
                        </h3>
                        <ol class="order-notice__list order-notice__list--numbered">
                          <li
                            v-for="(item, index) in orderNoticePaymentItems"
                            :key="'payment-' + index"
                          >
                            {{ item }}
                          </li>
                        </ol>
                      </div>
                    </section>
                  </template>

                  <!-- 详情视图 -->
                  <article v-else-if="activeNews" class="news-detail">
                    <button type="button" class="news-detail__back" @click="closeNews">
                      <i class="fa fa-arrow-left" aria-hidden="true" />
                      {{ $t('news.back') }}
                    </button>

                    <header class="news-detail__header">
                      <time class="news-detail__date" :datetime="activeNews.date">
                        {{ formatDate(activeNews.date) }}
                      </time>
                      <h2 class="news-detail__title">{{ activeNews.title }}</h2>
                    </header>

                    <div class="news-detail__body">
                      <p
                        v-for="(paragraph, pIndex) in activeNews.paragraphs"
                        :key="pIndex"
                      >
                        <template
                          v-for="(line, lIndex) in displayLines(paragraph)"
                          :key="lIndex"
                        >
                          <br v-if="lIndex > 0" />
                          {{ line }}
                        </template>
                      </p>
                    </div>
                  </article>
                </section>
              </div>
            </article>
          </section>
        </div>
      </main>

      <SiteFooter />
    </div>
  </div>
</template>

<script>
import SiteHeader from '@/components/SiteHeader.vue'
import SiteFooter from '@/components/SiteFooter.vue'
import {
  fetchPublicNews,
  newsContentToParagraphs,
  newsDateFromRecord,
  newsParagraphToDisplayLines
} from '@/api/news'

export default {
  name: 'NewsPage',
  components: { SiteHeader, SiteFooter },
  data() {
    return {
      newsItems: [],
      activeId: null,
      loading: true,
      loadError: ''
    }
  },
  computed: {
    activeNews() {
      return this.newsItems.find((item) => item.id === this.activeId) ?? null
    },
    orderNoticeCustomizationItems() {
      const items = this.$tm('news.orderNotice.customizationItems')
      return Array.isArray(items) ? items : []
    },
    orderNoticePaymentItems() {
      const items = this.$tm('news.orderNotice.paymentItems')
      return Array.isArray(items) ? items : []
    }
  },
  watch: {
    currentLocale() {
      document.title = this.$t('pageTitle.news')
    },
    '$route.query.id': {
      immediate: true,
      handler(id) {
        if (!id) {
          this.activeId = null
          return
        }
        const numId = Number(id)
        const found = this.newsItems.some((item) => item.id === numId)
        this.activeId = found ? numId : null
      }
    }
  },
  async created() {
    await this.loadNews()
  },
  mounted() {
    document.title = this.$t('pageTitle.news')
    document.body.classList.add('news')
  },
  beforeUnmount() {
    document.body.classList.remove('news')
  },
  methods: {
    normalizeNewsItem(row) {
      return {
        id: row.id,
        date: newsDateFromRecord(row),
        title: row.title,
        paragraphs: newsContentToParagraphs(row.content)
      }
    },
    async loadNews() {
      this.loading = true
      this.loadError = ''
      try {
        const res = await fetchPublicNews()
        const items = res.data?.items || []
        this.newsItems = items.map((row) => this.normalizeNewsItem(row))
        const queryId = this.$route.query.id
        if (queryId) {
          const numId = Number(queryId)
          this.activeId = this.newsItems.some((item) => item.id === numId) ? numId : null
        }
      } catch (e) {
        this.loadError = e.message || this.$t('news.loadError')
        this.newsItems = []
      } finally {
        this.loading = false
      }
    },
    openNews(id) {
      this.activeId = id
      this.$router.replace({ path: '/news', query: { id: String(id) } }).catch(() => {})
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    closeNews() {
      this.activeId = null
      this.$router.replace({ path: '/news' }).catch(() => {})
    },
    formatDate(isoDate) {
      const [y, m, d] = isoDate.split('-').map(Number)
      const date = new Date(y, m - 1, d)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    displayLines(paragraph) {
      return newsParagraphToDisplayLines(paragraph)
    }
  }
}
</script>

<style scoped>
.news-content {
  width: 100%;
}

.news-row-max {
  max-width: 100%;
}

.news-section {
  padding: 20px 30px 48px;
}

.news-intro {
  margin: 0 0 24px;
  color: var(--mmg-text, #747474);
  font-size: 17px;
}

.news-empty {
  margin: 0;
  color: var(--mmg-text, #747474);
  font-size: 17px;
}
</style>
